import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Progress,
  Tooltip,
  ListGroup,
  ListGroupItem
} from "shards-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faQuestionCircle,
  faSync,
  faEye,
  faEnvelope,
  faCalendarAlt,
  faUser
} from "@fortawesome/free-solid-svg-icons";

const TrustScoreIndicator = ({ email, name, trustData, onVerify, isVerifying }) => {
  const [expanded, setExpanded] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const toggleTooltip = () => {
    setTooltipOpen(!tooltipOpen);
  };

  const getScoreColor = (score) => {
    if (!score && score !== 0) return "secondary";
    if (score >= 85) return "success";
    if (score >= 65) return "primary";
    if (score >= 40) return "warning";
    return "danger";
  };

  const getScoreIcon = (score) => {
    if (!score && score !== 0) return faQuestionCircle;
    if (score >= 85) return faCheckCircle;
    if (score >= 65) return faCheckCircle;
    if (score >= 40) return faExclamationTriangle;
    return faTimesCircle;
  };

  const handleVerifyClick = () => {
    onVerify(email, name);
  };

  return (
    <Card className="trust-score-card mb-0" style={{ 
      borderRadius: "6px", 
      boxShadow: "0 1px 6px rgba(0,0,0,0.05)", 
      border: "1px solid #eaedf3" 
    }}>
      <CardHeader className="d-flex align-items-center border-bottom py-2" style={{
        backgroundColor: "#F8F9FA",
        borderBottom: "1px solid #eaedf3"
      }}>
        <FontAwesomeIcon icon={faShieldAlt} className="mr-2" style={{ color: "#21AA73" }} />
        <h6 className="m-0" style={{ fontSize: "14px", fontWeight: "600", color: "#3D5170" }}>Trust Analysis</h6>
        <div className="ml-auto">
          <FontAwesomeIcon 
            icon={faQuestionCircle} 
            id="trustScoreTooltip" 
            style={{ color: "#818ea3", marginRight: "10px", cursor: "pointer", fontSize: "14px" }}
            onClick={toggleTooltip}
          />
          <Tooltip
            open={tooltipOpen}
            target="#trustScoreTooltip"
            toggle={toggleTooltip}
            placement="left"
            className="trust-score-tooltip"
          >
            <div style={{ fontSize: "11px", maxWidth: "200px", textAlign: "left" }}>
              The Trust Score analyzes email reputation and other factors to help assess the legitimacy of the disputant.
              <ul style={{ paddingLeft: "15px", marginTop: "5px", marginBottom: "0" }}>
                <li>85-100: High Trust</li>
                <li>65-84: Medium Trust</li>
                <li>40-64: Low Trust</li>
                <li>0-39: Very Low Trust</li>
              </ul>
            </div>
          </Tooltip>
          
          {trustData && (
            <Button 
              size="sm" 
              theme="light" 
              style={{ padding: "2px 6px", fontSize: "11px" }}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Hide Details" : "Details"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody className="p-3">
        {isVerifying ? (
          <div className="text-center py-2">
            <FontAwesomeIcon icon={faSync} size="lg" style={{ color: "#21AA73" }} spin />
            <p className="mt-2 mb-0" style={{ fontSize: "13px", color: "#818ea3" }}>Analyzing email...</p>
          </div>
        ) : trustData ? (
          <div>
            <div className="d-flex align-items-center mb-2">
              <div style={{ minWidth: "85px" }}>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon 
                    icon={getScoreIcon(trustData.trustScore)} 
                    style={{ 
                      fontSize: "16px", 
                      color: trustData.trustScore >= 85 ? "#21AA73" : 
                             trustData.trustScore >= 65 ? "#21AA73" : 
                             trustData.trustScore >= 40 ? "#F98A01" : "#C81712",
                      marginRight: "8px"
                    }} 
                  />
                  <h4 style={{ 
                    fontSize: "18px", 
                    fontWeight: "600", 
                    margin: 0,
                    color: trustData.trustScore >= 85 ? "#21AA73" : 
                           trustData.trustScore >= 65 ? "#21AA73" : 
                           trustData.trustScore >= 40 ? "#F98A01" : "#C81712"
                  }}>
                    {trustData.trustScore}%
                  </h4>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <Progress
                  value={trustData.trustScore}
                  style={{ 
                    height: "6px", 
                    backgroundColor: "#f1f3f5",
                    borderRadius: "6px",
                    background: "linear-gradient(to right, #C81712, #F98A01, #21AA73)",
                    overflow: "visible"
                  }}
                />
                <div style={{ 
                  width: "12px", 
                  height: "12px", 
                  backgroundColor: "#fff", 
                  border: "2px solid #21AA73",
                  borderRadius: "50%", 
                  position: "relative", 
                  top: "-9px", 
                  left: `calc(${trustData.trustScore}% - 6px)` 
                }}></div>
              </div>
            </div>

            <div className="mb-2">
              <p className="mb-0" style={{ 
                fontSize: "13px", 
                color: "#495057",
                backgroundColor: "rgba(33, 170, 115, 0.05)",
                border: "1px solid rgba(33, 170, 115, 0.1)",
                borderRadius: "4px",
                padding: "6px 10px"
              }}>
                {trustData.recommendation.text}
              </p>
            </div>

            {expanded && (
              <>
                <div style={{ 
                  fontSize: "11px", 
                  textTransform: "uppercase", 
                  color: "#818ea3", 
                  marginTop: "12px", 
                  marginBottom: "4px",
                  fontWeight: "500"
                }}>
                  Email Details
                </div>
                <ListGroup flush style={{ marginBottom: "10px" }}>
                  <ListGroupItem className="px-0 py-1" style={{ borderColor: "#eaedf3" }}>
                    <div className="d-flex">
                      <FontAwesomeIcon icon={faEnvelope} className="mr-2" style={{ color: "#818ea3", width: "16px" }} />
                      <div style={{ fontSize: "13px" }}>
                        <strong style={{ color: "#495057" }}>Email</strong>
                        <div style={{ color: "#495057" }}>{email}</div>
                      </div>
                      <div className="ml-auto">
                        {trustData.summary.disposable && (
                          <span style={{
                            fontSize: "10px",
                            padding: "2px 5px",
                            borderRadius: "3px",
                            backgroundColor: "rgba(200, 23, 18, 0.1)",
                            color: "#C81712",
                            fontWeight: "500"
                          }}>Disposable</span>
                        )}
                        {trustData.summary.suspicious && (
                          <span style={{
                            fontSize: "10px",
                            padding: "2px 5px",
                            borderRadius: "3px",
                            backgroundColor: "rgba(249, 138, 1, 0.1)",
                            color: "#F98A01",
                            fontWeight: "500",
                            marginLeft: "4px"
                          }}>Suspicious</span>
                        )}
                      </div>
                    </div>
                  </ListGroupItem>
                  
                  <ListGroupItem className="px-0 py-1" style={{ borderColor: "#eaedf3" }}>
                    <div className="d-flex">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" style={{ color: "#818ea3", width: "16px" }} />
                      <div style={{ fontSize: "13px" }}>
                        <strong style={{ color: "#495057" }}>First Seen</strong>
                        <div style={{ color: "#495057" }}>{trustData.summary.firstSeen || "Unknown"}</div>
                      </div>
                    </div>
                  </ListGroupItem>
                  
                  {trustData.summary.profiles && trustData.summary.profiles.length > 0 && (
                    <ListGroupItem className="px-0 py-1" style={{ borderColor: "#eaedf3" }}>
                      <div className="d-flex">
                        <FontAwesomeIcon icon={faUser} className="mr-2" style={{ color: "#818ea3", width: "16px" }} />
                        <div style={{ fontSize: "13px" }}>
                          <strong style={{ color: "#495057" }}>Associated Profiles</strong>
                          <div style={{ color: "#495057" }}>{trustData.summary.profiles.join(", ")}</div>
                        </div>
                      </div>
                    </ListGroupItem>
                  )}
                </ListGroup>
              </>
            )}
            
            {expanded && (
              <div className="text-right">
                <Button 
                  size="sm" 
                  style={{ 
                    fontSize: "11px", 
                    padding: "3px 8px", 
                    backgroundColor: "transparent",
                    color: "#21AA73",
                    border: "1px solid rgba(33, 170, 115, 0.3)",
                  }} 
                  onClick={handleVerifyClick}
                >
                  <FontAwesomeIcon icon={faSync} className="mr-1" />
                  Refresh
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-2">
            <Button 
              theme="outline-primary" 
              size="sm"
              style={{ 
                borderColor: "#21AA73", 
                color: "#21AA73", 
                fontSize: "13px",
                padding: "6px 12px" 
              }}
              onClick={handleVerifyClick}
            >
              <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
              Analyze Dispute
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default TrustScoreIndicator; 